import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Action, Location } from "history";
import history from "utils/history";
import { store } from "utils/store";

export interface UseLeaveGuardOptions {
  /** A comparison is running or a result is open: only then is anything held. */
  active: boolean;
  /** A PUSH/REPLACE to the page's own pathname+search (the side-nav item on this page): "start over", no dialog. */
  onSameLocation: () => void;
}

export interface LeaveGuard {
  /** A navigation is being held: the page shows its confirm dialog. */
  pending: boolean;
  /** Let the held navigation through (replayed with the same action, path, query and router state). */
  confirmLeave: () => void;
  /** Drop the held navigation. */
  stay: () => void;
  /** Remove the guard NOW, synchronously, through a ref (the page calls it before `checkStatus` logs the user out on a 401). */
  release: () => void;
  /** Run a navigation of the page's own (the phone contract sheet pushes a history entry) without it being held or counted as "start over". */
  bypass: <T>(run: () => T) => T;
}

interface HeldNavigation {
  location: Location;
  action: Action;
}

// Logged out (the 401 logout, the menu logout, the SSO logout) means there is nothing left to protect.
const isLoggedIn = (): boolean => Boolean(store.getState().auth.accessToken);

const isSamePage = (
  next: Pick<Location, "pathname" | "search">,
  current: Pick<Location, "pathname" | "search">,
): boolean =>
  next.pathname === current.pathname && next.search === current.search;

/**
 * Asks before the user leaves the Compare page while a comparison runs or a result is open.
 *
 * The blocker holds a navigation by returning `false`, never a string: history v4 turns a string into
 * `window.confirm`, and the page shows its own dialog instead. It sees PUSH and REPLACE as well as POP,
 * because leaving through the side nav is a PUSH. The guard is armed only while `active` and, once
 * released or confirmed, stays off until `active` turns false and true again (a new comparison).
 */
export const useLeaveGuard = ({
  active,
  onSameLocation,
}: UseLeaveGuardOptions): LeaveGuard => {
  const [pending, setPending] = useState<boolean>(false);
  const heldRef = useRef<HeldNavigation | null>(null);
  // Removes the blocker and the beforeunload listener; null while the guard is not armed.
  const disarmRef = useRef<(() => void) | null>(null);
  // Set by `release` / `confirmLeave`: the effect must not arm again until `active` cycles.
  const releasedRef = useRef<boolean>(false);
  const bypassDepthRef = useRef<number>(0);
  const onSameLocationRef = useRef<() => void>(onSameLocation);
  onSameLocationRef.current = onSameLocation;

  useEffect(() => {
    if (!active) {
      releasedRef.current = false;
      return undefined;
    }
    if (releasedRef.current) return undefined;

    const block = (next: Location, action: Action): false | undefined => {
      if (bypassDepthRef.current > 0) return undefined;
      if (!isLoggedIn()) return undefined;
      if (isSamePage(next, history.location)) {
        // POP to the page's own location is the phone contract sheet's Back: nothing to ask, nothing to reset.
        if (action !== "POP") onSameLocationRef.current();
        return undefined;
      }
      heldRef.current = { location: next, action };
      setPending(true);
      return false;
    };

    // Native prompt only: browsers allow no custom dialog on a tab close or refresh. After a logout (an SSO
    // logout redirects with a full page load) there is nothing to protect, so no prompt.
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isLoggedIn()) return;
      event.preventDefault();
      event.returnValue = "";
    };

    const unblock = history.block(block);
    window.addEventListener("beforeunload", warnBeforeUnload);

    const disarm = () => {
      unblock();
      window.removeEventListener("beforeunload", warnBeforeUnload);
      if (disarmRef.current === disarm) disarmRef.current = null;
    };
    disarmRef.current = disarm;

    return () => {
      disarm();
      heldRef.current = null;
      setPending(false);
    };
  }, [active]);

  const stay = useCallback(() => {
    heldRef.current = null;
    setPending(false);
  }, []);

  const release = useCallback(() => {
    const disarm = disarmRef.current;
    if (!disarm) return;
    releasedRef.current = true;
    disarm();
    heldRef.current = null;
    setPending(false);
  }, []);

  const confirmLeave = useCallback(() => {
    const held = heldRef.current;
    heldRef.current = null;
    setPending(false);
    if (!held) return;
    // The blocker goes first, or the replay would be held again.
    const disarm = disarmRef.current;
    if (disarm) {
      releasedRef.current = true;
      disarm();
    }
    // A held POP has already been reverted in place by history, so `goBack()` could walk the wrong way (or one
    // entry too far); replaying it as a PUSH always lands on the intended page, like InPersonSessionShell does.
    if (held.action === "REPLACE") history.replace(held.location);
    else history.push(held.location);
  }, []);

  // Synchronous on purpose: a navigation that starts inside `run` is seen by the blocker while the counter is up.
  const bypass = useCallback(<T>(run: () => T): T => {
    bypassDepthRef.current += 1;
    try {
      return run();
    } finally {
      bypassDepthRef.current -= 1;
    }
  }, []);

  return useMemo(
    () => ({ pending, confirmLeave, stay, release, bypass }),
    [pending, confirmLeave, stay, release, bypass],
  );
};
